<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, getCurrentInstance, onBeforeMount, onMounted, ref } from 'vue';

import type { Selection } from '@/types/configuration';
import type { Product } from '@/types/products';

import CarrouselListCard from '@/components/carousel-list/CarouselListCard.vue';
import CarrouselListSlider from '@/components/carousel-list/CarouselListSlider.vue';
import { useEditor } from '@/composables/useEditor';
import { i18n } from '@/shared-instance';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useProductsStore } from '@/stores/useProductsStore';

const props = defineProps<{
  selection?: string;
}>();

const { configToFilter } = useEditor();
const configurationStore = useGlobalConfigurationStore();
const { globalConfiguration } = storeToRefs(configurationStore);
const productsStore = useProductsStore();

const prefixUrl = computed(() => globalConfiguration.value?.prefixUrl ?? null);

const selection = ref<Selection>();
const products = ref<Product[]>([]);

i18n.install(getCurrentInstance()!.appContext.app);

const showFavorites = computed(() => globalConfiguration.value?.options?.hasFavorites !== false);

onBeforeMount(async () => {
  if (props.selection == null) {
    throw new Error('No config provided for carrousel.');
  }
  selection.value = JSON.parse(props.selection);
  if (!globalConfiguration.value || !globalConfiguration.value.apikey)
    throw new Error('No config provided for carrousel.');
});

onMounted(async () => {
  if (selection.value == null) {
    throw new Error('Carrousel config is missing');
  }
  const result = await productsStore.getProductsList({
    size: 100,
    metrics: [
      {
        type: 'taxonomyCount',
        taxonomyTypes: [
          'TYPES',
          'THEMES',
          'PLACES',
        ],
      },
    ],
    properties: [{ type: 'rating' }, { type: 'nextSessionDate' }, { type: 'product_enrichment_by_distributor' }],
    filters: configToFilter(selection.value),
    sorts: globalConfiguration.value?.apikey === 'YoubookFFVyBGWEQKbWKTJwTArIg9YcAqVEXaSohwM6LZ4FPREkHZg3BWKAB9hm0' ? [{ type: 'random' }] : [{ type: 'next_session' }],
    selections: [],
  });
  products.value = productsStore.formatToProducts(result.data);
});
</script>

<template>
  <CarrouselListSlider :items="products" :items-per-page="4" aria-label="Liste des activités">
    <template #item="{ item }">
      <CarrouselListCard
        :product="item"
        :prefix-url="prefixUrl"
        :show-favorite="showFavorites"
      />
    </template>
  </CarrouselListSlider>
</template>
